import { useEffect } from "react";
import type { RefObject } from "react";

import { useReducedMotion } from "@/hooks/useReducedMotion";

type MotionCoverSplitOptions = {
  sectionRef: RefObject<HTMLDivElement | null>;
  shellRef: RefObject<HTMLDivElement | null>;
  topPanelRef: RefObject<HTMLDivElement | null>;
  bottomPanelRef: RefObject<HTMLDivElement | null>;
  topImageRef: RefObject<HTMLImageElement | null>;
  bottomImageRef: RefObject<HTMLImageElement | null>;
  videoShellRef: RefObject<HTMLDivElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
};

const DESKTOP_MEDIA = "(min-width: 768px)";

export function useMotionCoverSplit({
  sectionRef,
  shellRef,
  topPanelRef,
  bottomPanelRef,
  topImageRef,
  bottomImageRef,
  videoShellRef,
  videoRef,
}: MotionCoverSplitOptions) {
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const section = sectionRef.current;
    const shell = shellRef.current;
    const topPanel = topPanelRef.current;
    const bottomPanel = bottomPanelRef.current;
    const topImage = topImageRef.current;
    const bottomImage = bottomImageRef.current;
    const videoShell = videoShellRef.current;
    const video = videoRef.current;

    if (!section || !shell || !topPanel || !bottomPanel || !topImage || !bottomImage || !videoShell || !video) {
      return;
    }

    const desktopQuery = window.matchMedia(DESKTOP_MEDIA);
    const homeStage = document.getElementById("homeStage");
    const viewport = document.querySelector<HTMLElement>("[data-stage-viewport]");
    const stageSection = section.closest<HTMLElement>("[data-stage-section]") ?? section;
    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);

    let frameId: number | null = null;

    const resetStyles = () => {
      shell.style.opacity = "1";
      shell.style.visibility = "visible";
      topPanel.style.opacity = "1";
      bottomPanel.style.opacity = "1";
      topPanel.style.transform = "translate3d(0, 0, 0)";
      bottomPanel.style.transform = "translate3d(0, 0, 0)";
      topImage.style.transform = "translate3d(0, 0, 0) scale(1)";
      bottomImage.style.transform = "translate3d(0, 0, 0) scale(1)";
      videoShell.style.opacity = "1";
      videoShell.style.setProperty("--motion-logo-backdrop-opacity", "1");
      video.style.opacity = "0";
      video.style.visibility = "hidden";
      video.currentTime = 0;
      video.pause();
    };

    const isHorizontalStage = () =>
      desktopQuery.matches || homeStage?.dataset.mobileStage === "horizontal";

    const computeHorizontalProgress = () => {
      if (!viewport) {
        return 0;
      }

      const rect = stageSection.getBoundingClientRect();
      const entryPoint = viewport.clientWidth * 0.46;
      const exitPoint = -stageSection.offsetWidth * 0.34;
      const travel = Math.max(entryPoint - exitPoint, 1);

      return clamp((entryPoint - rect.left) / travel, 0, 1);
    };

    const updateHorizontalPin = (horizontalStage: boolean) => {
      if (!horizontalStage || !viewport || stageSection === section) {
        section.style.transform = "";
        return;
      }

      const maximumTravel = Math.max(
        stageSection.offsetWidth - viewport.clientWidth,
        0,
      );
      const travelledInsideSection = clamp(
        viewport.scrollLeft - stageSection.offsetLeft,
        0,
        maximumTravel,
      );

      section.style.transform = `translate3d(${travelledInsideSection}px, 0, 0)`;
    };

    const computeMobileProgress = () => {
      const rect = section.getBoundingClientRect();
      const distance = window.innerHeight - rect.top;
      return clamp(distance / (rect.height * 0.98), 0, 1);
    };

    const render = () => {
      frameId = null;

      const horizontalStage = isHorizontalStage();
      updateHorizontalPin(horizontalStage);

      if (reducedMotion) {
        resetStyles();
        return;
      }

      const rawProgress = horizontalStage ? computeHorizontalProgress() : computeMobileProgress();
      const splitProgress = clamp(
        (rawProgress - (desktopQuery.matches ? 0.18 : 0.12)) /
          (desktopQuery.matches ? 0.5 : 0.24),
        0,
        1,
      );
      const hideProgress = clamp(
        (rawProgress - (desktopQuery.matches ? 0.38 : 0.34)) /
          (desktopQuery.matches ? 0.1 : 0.14),
        0,
        1,
      );
      const videoProgress = clamp(
        (rawProgress - (desktopQuery.matches ? 0.4 : 0.34)) /
          (desktopQuery.matches ? 0.12 : 0.16),
        0,
        1,
      );
      const backdropFadeProgress = clamp(
        (rawProgress - (desktopQuery.matches ? 0.28 : 0.26)) /
          (desktopQuery.matches ? 0.08 : 0.1),
        0,
        1,
      );
      const panelTravel = desktopQuery.matches ? 104 : 122;

      shell.style.opacity = `${1 - hideProgress}`;
      shell.style.visibility = hideProgress > 0.985 ? "hidden" : "visible";
      topPanel.style.opacity = `${1 - hideProgress}`;
      bottomPanel.style.opacity = `${1 - hideProgress}`;
      topPanel.style.transform = `translate3d(0, ${-splitProgress * panelTravel}%, 0)`;
      bottomPanel.style.transform = `translate3d(0, ${splitProgress * panelTravel}%, 0)`;

      topImage.style.transform = "translate3d(0, 0, 0) scale(1)";
      bottomImage.style.transform = "translate3d(0, 0, 0) scale(1)";
      videoShell.style.opacity = "1";
      videoShell.style.setProperty(
        "--motion-logo-backdrop-opacity",
        `${1 - backdropFadeProgress}`,
      );
      video.style.opacity = `${videoProgress}`;
      video.style.visibility = videoProgress > 0.02 ? "visible" : "hidden";

      if (videoProgress > 0.02) {
        void video.play().catch(() => {});
      } else {
        video.pause();
      }
    };

    const requestRender = () => {
      if (!frameId) {
        frameId = window.requestAnimationFrame(render);
      }
    };

    resetStyles();
    requestRender();

    viewport?.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("resize", requestRender);
    desktopQuery.addEventListener("change", requestRender);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      viewport?.removeEventListener("scroll", requestRender);
      window.removeEventListener("scroll", requestRender);
      window.removeEventListener("resize", requestRender);
      desktopQuery.removeEventListener("change", requestRender);
      section.style.transform = "";
    };
  }, [bottomImageRef, bottomPanelRef, reducedMotion, sectionRef, shellRef, topImageRef, topPanelRef]);
}
